import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, KeyRound } from "lucide-react";
import { useParentAuth } from "@/context/ParentAuthContext";

export default function ParentSettings() {
  const { parent, refresh } = useParentAuth();
  const [notify, setNotify] = useState(true);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    if (parent) {
      setNotify(parent.notify_new_events ?? true);
      setName(parent.name || "");
    }
  }, [parent]);

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.patch("/parent/me", { name, notify_new_events: notify });
      await refresh();
      toast.success("Saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/parent/password/change", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Change failed"); }
    finally { setPwBusy(false); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display font-black text-3xl tracking-tight">Settings</h1>
      <p className="mt-2 text-[#7A7D8C]">Account preferences and notifications.</p>

      <div className="mt-8 bg-white rounded-2xl border border-slate-100 p-6" data-testid="settings-profile">
        <h2 className="font-display font-bold text-lg">Profile</h2>
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Your name</label>
            <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Email</label>
            <input value={parent?.email || ""} disabled className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 text-slate-500 px-3 py-2.5 text-sm" />
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-6" data-testid="settings-notifications">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#FF9F1C]" />
          <h2 className="font-display font-bold text-lg">Notifications</h2>
        </div>
        <label className="mt-4 flex items-start gap-3 cursor-pointer">
          <input type="checkbox" data-testid="settings-notify" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-1 w-4 h-4 accent-[#FF9F1C]" />
          <span>
            <span className="font-semibold">Email me when a new event is announced</span>
            <div className="text-xs text-[#7A7D8C]">We'll ping you as soon as a new competition or summer camp goes live.</div>
          </span>
        </label>
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={savePrefs} disabled={saving} data-testid="settings-save" className="px-6 py-2.5 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_8px_20px_rgb(255,159,28,0.3)] disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
      </div>

      <div className="mt-10 bg-white rounded-2xl border border-slate-100 p-6" data-testid="settings-password">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#FF9F1C]" />
          <h2 className="font-display font-bold text-lg">Change password</h2>
        </div>
        <form onSubmit={changePw} className="mt-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Current password</label>
            <input data-testid="settings-pw-current" type="password" value={pw.current_password} required onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">New password (8+ characters)</label>
            <input data-testid="settings-pw-new" type="password" value={pw.new_password} required onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm" />
          </div>
          <div className="flex justify-end">
            <button disabled={pwBusy} data-testid="settings-pw-save" className="px-5 py-2 rounded-full bg-[#1E1E24] text-white text-sm font-bold disabled:opacity-60">{pwBusy ? "Updating…" : "Update password"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
